import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './ImageLightbox.scss';
import LoadingSpinner from '../common/LoadingSpinner';

interface ImageData {
  url: string;
  thumbnailUrl: string;
  isEnhancing?: boolean;
  originalUrl?: string;
  enhancedUrl?: string;
  originalThumbnailUrl?: string;
  enhancedThumbnailUrl?: string;
}

interface ImageLightboxProps {
  isOpen: boolean;
  images: ImageData[];
  initialIndex: number;
  onClose: () => void;
  onToggleVersion?: (index: number) => void;
  onRegenerate?: (index: number) => void;
  isRegenerating?: boolean;
  enhancementCredits?: number | null;
  onEnhance?: (index: number) => void;
  onBuyCredits?: () => void;
}

/**
 * Full-screen image viewer with original/enhanced toggle, compare slider and
 * re-enhance. For a non-enhanced image it offers "Enhance" when credits are
 * left, or a "Buy credits" block only when the host wires `onBuyCredits`, so
 * hosts without a checkout flow never show a dead button (JAA-394).
 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  isOpen,
  images,
  initialIndex,
  onClose,
  onToggleVersion,
  onRegenerate,
  isRegenerating,
  enhancementCredits,
  onEnhance,
  onBuyCredits,
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isComparing, setIsComparing] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Update current index when initialIndex changes
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // Reset comparison mode when switching images
  useEffect(() => {
    setIsComparing(false);
    setSliderPosition(50);
  }, [currentIndex]);

  // Clamp index when images change and close if there are no images
  useEffect(() => {
    if (!isOpen) return;
    if (!images || images.length === 0) {
      onClose();
      return;
    }
    if (currentIndex >= images.length) {
      setCurrentIndex(images.length - 1);
    } else if (currentIndex < 0) {
      setCurrentIndex(0);
    }
  }, [isOpen, images, currentIndex, onClose]);

  // Preload both original and enhanced versions for the current image to speed up toggling
  useEffect(() => {
    if (!isOpen) return;
    const img = images[currentIndex];
    if (!img) return;
    const urls: string[] = [];
    if (img.originalUrl) urls.push(img.originalUrl);
    if (img.enhancedUrl) urls.push(img.enhancedUrl);
    urls.forEach((src) => {
      const pre = new Image();
      pre.src = src;
    });
  }, [isOpen, currentIndex, images]);

  // Compute current image early so hooks below are not conditional
  const currentImage = images[currentIndex];

  // When the displayed URL changes in single view, show a loading indicator until the image loads
  useEffect(() => {
    if (!isOpen) return;
    if (!isComparing) {
      setIsImageLoading(true);
    }
  }, [isOpen, currentImage?.url, isComparing]);

  // Navigation functions
  const navigatePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const navigateNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  const handleToggleVersion = useCallback(() => {
    if (onToggleVersion) {
      onToggleVersion(currentIndex);
    }
  }, [currentIndex, onToggleVersion]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          navigatePrevious();
          break;
        case 'ArrowRight':
          navigateNext();
          break;
        case ' ':
          e.preventDefault();
          if (images[currentIndex]?.enhancedUrl) {
            handleToggleVersion();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images, onClose, navigatePrevious, navigateNext, handleToggleVersion]);

  const handleComparisonSliderMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;

    const container = e.currentTarget as HTMLElement;
    const rect = container.getBoundingClientRect();
    let clientX: number;

    if ('touches' in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }

    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, [isDragging]);

  const startDragging = useCallback(() => {
    setIsDragging(true);
  }, []);

  const stopDragging = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      const handleMouseUp = () => stopDragging();
      const handleTouchEnd = () => stopDragging();

      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, stopDragging]);

  // Early compute enhancement flag
  const hasEnhancement = currentImage?.enhancedUrl && currentImage.originalUrl && currentImage.enhancedUrl;
  const isEnhancementPending = Boolean(currentImage?.isEnhancing && !currentImage?.enhancedUrl);

  // When the displayed URL changes in single view, show a loading indicator until the image fires onLoad
  useEffect(() => {
    if (!isOpen) return;
    if (!isComparing) {
      setIsImageLoading(true);
    }
  }, [isOpen, currentImage?.url, isComparing]);

  if (!isOpen) return null;

  return createPortal(
    <div className="image-lightbox" onClick={onClose}>
      <div className="image-lightbox__container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="image-lightbox__header">
          <div className="image-lightbox__counter">
            {currentIndex + 1} / {images.length}
          </div>

          {hasEnhancement && (
            <div className="image-lightbox__controls">
              {/*               <button
                className={`image-lightbox__view-mode ${!isComparing ? 'is-active' : ''}`}
                onClick={() => setIsComparing(false)}
                title={t('building.offMarket.modal.singleView')}
                aria-label={t('building.offMarket.modal.singleView')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>{t('building.offMarket.modal.singleView')}</span>
              </button>
              
              <button
                className={`image-lightbox__view-mode ${isComparing ? 'is-active' : ''}`}
                onClick={() => {
                  setIsComparing(true);
                  setSliderPosition(50);
                }}
                title={t('building.offMarket.modal.compareView')}
                aria-label={t('building.offMarket.modal.compareView')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3v18M3 3h18v18H3V3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>{t('building.offMarket.modal.compareView')}</span>
              </button> */}
            </div>
          )}

          <button className="image-lightbox__close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Image Viewer */}
        <div className="image-lightbox__viewer">
          {isComparing && hasEnhancement ? (
            // Comparison slider view
            <div
              className="image-lightbox__comparison"
              onMouseMove={handleComparisonSliderMove}
              onTouchMove={handleComparisonSliderMove}
            >
              <div className="image-lightbox__comparison-container">
                {/* Original Image (full width) */}
                <img
                  src={currentImage.originalUrl}
                  alt="Original"
                  className="image-lightbox__comparison-image image-lightbox__comparison-image--original"
                />

                {/* Enhanced Image (clipped) */}
                <div
                  className="image-lightbox__comparison-overlay"
                  style={{ width: `${sliderPosition}%` }}
                >
                  <img
                    src={currentImage.enhancedUrl}
                    alt="Enhanced"
                    className="image-lightbox__comparison-image image-lightbox__comparison-image--enhanced"
                  />
                </div>

                {/* Slider Handle */}
                <div
                  className="image-lightbox__comparison-slider"
                  style={{ left: `${sliderPosition}%` }}
                  onMouseDown={startDragging}
                  onTouchStart={startDragging}
                >
                  <div className="image-lightbox__comparison-slider-line" />
                  <div className="image-lightbox__comparison-slider-handle">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 9l-3 3 3 3M16 9l3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {/* Labels */}
                <div className="image-lightbox__comparison-labels">
                  <div className="image-lightbox__comparison-label image-lightbox__comparison-label--left">
                    {t('building.offMarket.modal.original')}
                  </div>
                  <div className="image-lightbox__comparison-label image-lightbox__comparison-label--right">
                    {t('building.offMarket.modal.enhanced')}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Single image view
            <div className="image-lightbox__single">
              {currentImage && (
                <img
                  src={currentImage.url}
                  alt={currentImage.url === currentImage.enhancedUrl ? 'Enhanced' : 'Original'}
                  className="image-lightbox__image"
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => setIsImageLoading(false)}
                />
              )}

              {isImageLoading && (
                <div
                  className="image-lightbox__loading-overlay"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    background: 'transparent'
                  }}
                >
                  <LoadingSpinner />
                </div>
              )}

              {/*               {hasEnhancement && (
                <div className="image-lightbox__badge">
                  {currentImage.url === currentImage.enhancedUrl ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {t('building.offMarket.modal.aiEnhanced')}
                    </>
                  ) : (
                    t('building.offMarket.modal.original')
                  )}
                </div>
              )} */}
            </div>
          )}

          <>
            {!isComparing && !!currentImage?.enhancedUrl && (
              <div className="image-lightbox__actions">
                {onRegenerate && currentImage.url === currentImage.enhancedUrl && (
                  <>
                    <button
                      className="image-lightbox__regenerate"
                      onClick={() => onRegenerate(currentIndex)}
                      disabled={isRegenerating}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {isRegenerating
                        ? t('building.offMarket.modal.regenerating')
                        : t('building.offMarket.modal.regenerate')
                      }
                    </button>
                    {typeof enhancementCredits === 'number' && (
                      <p className="image-lightbox__credits-info">
                        {enhancementCredits > 0
                          ? t('building.offMarket.modal.regenerateCreditInfo', { count: enhancementCredits })
                          : t('building.offMarket.modal.regenerateNoCreditInfo')
                        }
                      </p>
                    )}
                  </>
                )}
                <button
                  className="image-lightbox__toggle"
                  onClick={handleToggleVersion}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {currentImage.url === currentImage.enhancedUrl ?
                    t('building.offMarket.modal.showOriginal') :
                    t('building.offMarket.modal.showEnhanced')
                  }
                </button>
              </div>
            )}
          </>

          {!isComparing && isEnhancementPending && (
            <div className="image-lightbox__actions">
              <button
                className="image-lightbox__toggle image-lightbox__toggle--pending"
                type="button"
                disabled
                aria-busy="true"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="image-lightbox__toggle-content">
                  {t('building.offMarket.modal.showEnhanced')}
                  <span className="image-lightbox__button-spinner" aria-hidden="true" />
                </span>
              </button>
            </div>
          )}

          {/* Non-enhanced image: offer enhancement (with credits) or buying credits (only when wired) */}
          {!isComparing
            && !currentImage?.enhancedUrl
            && !isEnhancementPending
            && onEnhance
            && typeof enhancementCredits === 'number'
            && (
              enhancementCredits > 0 ? (
                <div className="image-lightbox__actions">
                  <button
                    className="image-lightbox__regenerate"
                    onClick={() => onEnhance(currentIndex)}
                    disabled={isRegenerating}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 3v4M3 5h4M6 17v4m-2-2h4M13 3l1.9 5.1L20 10l-5.1 1.9L13 17l-1.9-5.1L6 10l5.1-1.9L13 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {isRegenerating
                      ? t('building.offMarket.modal.enhancing')
                      : t('building.offMarket.modal.enhanceThisImage')
                    }
                  </button>
                  <p className="image-lightbox__credits-info">
                    {t('building.offMarket.modal.regenerateCreditInfo', { count: enhancementCredits })}
                  </p>
                </div>
              ) : onBuyCredits && (
                <div className="image-lightbox__buy">
                  <p className="image-lightbox__buy-title">
                    {t('building.offMarket.modal.noEnhancementCreditsLeft')}
                  </p>
                  <p className="image-lightbox__buy-subtitle">
                    {t('building.offMarket.modal.buyCreditsToEnhance')}
                  </p>
                  <button
                    className="image-lightbox__buy-button"
                    onClick={() => onBuyCredits?.()}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
                      <path d="M2 10h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {t('building.offMarket.modal.buyCredits')}
                  </button>
                </div>
              )
            )}

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                className="image-lightbox__nav image-lightbox__nav--prev"
                onClick={navigatePrevious}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <button
                className="image-lightbox__nav image-lightbox__nav--next"
                onClick={navigateNext}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Thumbnail Strip */}
        {images.length > 1 && (
          <div className="image-lightbox__thumbnails">
            <div className="image-lightbox__thumbnails-inner">
              {images.map((image, index) => (
                <button
                  key={index}
                  className={`image-lightbox__thumbnail ${index === currentIndex ? 'is-active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                >
                  <img
                    src={image.thumbnailUrl}
                    alt={`Thumbnail ${index + 1}`}
                  />
                  {image.enhancedUrl && image.url === image.enhancedUrl && (
                    <div className="image-lightbox__thumbnail-badge">
{/*                       <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg> */}
                      <img src="/assets/jaakko_head.png" height="12" alt="" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
